import { Search, Filter, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CATEGORIES = [
  { value: "domain_report", label: "Domain Report" },
  { value: "deep_research", label: "Deep Research" },
  { value: "strategic_framework", label: "Strategic Framework" },
];

const AUTHORS = [
  { value: "BioCreative AI Hub", label: "BioCreative AI Hub" },
  { value: "CARR Strategy Team", label: "CARR Strategy Team" },
];

export interface ResearchFilters {
  search: string;
  category: string[];
  author: string[];
}

interface ResearchFilterSidebarProps {
  filters: ResearchFilters;
  onFiltersChange: (filters: ResearchFilters) => void;
  totalCount: number;
  authorOptions?: string[];
}

export function ResearchFilterSidebar({ filters, onFiltersChange, totalCount, authorOptions }: ResearchFilterSidebarProps) {
  const hasActiveFilters = filters.search || filters.category.length > 0 || filters.author.length > 0;

  const toggleArray = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const authors = authorOptions && authorOptions.length > 0
    ? authorOptions.map((a) => ({ value: a, label: a }))
    : AUTHORS;

  return (
    <div className="space-y-4 p-4 bg-card rounded-lg border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Filters</span>
          <Badge variant="secondary">{totalCount} docs</Badge>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFiltersChange({ search: "", category: [], author: [] })}
            className="h-8"
          >
            <X className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search documents..."
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          className="pl-9"
        />
      </div>

      <Accordion type="multiple" defaultValue={["category", "author"]} className="space-y-2">
        <AccordionItem value="category" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Category</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {CATEGORIES.map((c) => (
                <div key={c.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`rcat-${c.value}`}
                    checked={filters.category.includes(c.value)}
                    onCheckedChange={() =>
                      onFiltersChange({ ...filters, category: toggleArray(filters.category, c.value) })
                    }
                  />
                  <label htmlFor={`rcat-${c.value}`} className="text-sm leading-none">
                    {c.label}
                  </label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="author" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Author</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {authors.map((a) => (
                <div key={a.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`rauth-${a.value}`}
                    checked={filters.author.includes(a.value)}
                    onCheckedChange={() =>
                      onFiltersChange({ ...filters, author: toggleArray(filters.author, a.value) })
                    }
                  />
                  <label htmlFor={`rauth-${a.value}`} className="text-sm leading-none">
                    {a.label}
                  </label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
